import React from "react";

import {
  Card,
  CardContent,
  Stack,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import BankIdThisDeviceLogin from "./BankIdThisDeviceLogin";
import BankIdMobileLoginQr from "./BankIdMobileLoginQr";
import { cancelLogin, startBankIdLogin, checkIfBankIdLoginStarted } from "shared/bankidApi";
import { isMobileBrowser } from "shared/utils";

interface BankIdFrameProps {
  sx?: SxProps<Theme>;
}

const getLoginMethodFromLoginHint = (login_hint:string) => {
  if (login_hint === "" && isMobileBrowser()) {
    return "this_device";
  }
  return login_hint === "this_device" ? "this_device" : "mobile"
} 

const BankIdFrame: React.FC<BankIdFrameProps> = ({ sx = [] }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const login_challenge = searchParams.get("login_challenge") as string;
  const login_hint = searchParams.get("login_hint") ?? "";
  const [loginMethod, setLoginMethod] = React.useState(
    getLoginMethodFromLoginHint(login_hint)
  );
  const [loginFailed, setLoginFailed] = React.useState(false);
  const [bankIdOpened, setBankIdOpened] = React.useState(false);
  const [qrCode, setQrCode] = React.useState<string | null>(null);
  const bankIdUrl = React.useRef("");
  const [userMessage, setUserMessage] = React.useState("");
  const pollingCallback = React.useRef<NodeJS.Timeout | null>(null);

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLoginMethod: string | null
  ) => {
    if (newLoginMethod !== null) {
      setLoginMethod(newLoginMethod);
    }
  };

  const onBackClick = async (_event: React.MouseEvent<HTMLButtonElement>) => {
    if (login_challenge !== null) {
      try {
        await cancelLogin(login_challenge);
      } catch {
        // no meaningful error handling here, we just go back which should reset
        // everything
      }
    }
    setBankIdOpened(false);
    history.back();
  };

  const renderedLoginMethod = () => {
    switch (loginMethod) {
      case "this_device":
        return (
          <BankIdThisDeviceLogin
            bankIdOpened={bankIdOpened}
            setBankIdOpened={setBankIdOpened}
            onBackClick={onBackClick}
            loginFailed={loginFailed}
            startBankIdLogin={async (personalNumber?: string) => {
              await startBankIdLogin(
                login_challenge,
                pollingCallback,
                loginMethod,
                bankIdUrl,
                setBankIdOpened,
                setLoginFailed,
                setUserMessage,
                setQrCode,
                personalNumber
              );
            }}
            bankIdUrl={bankIdUrl}
            userMessage={userMessage}
          />
        );
      case "mobile":
        return (
          <BankIdMobileLoginQr
            onBackClick={onBackClick}
            loginFailed={loginFailed}
            qrCode={qrCode}
            userMessage={userMessage}
          />
        );
    }
  };

  React.useEffect(() => {
    if (loginMethod === "mobile") {
      startBankIdLogin(
        login_challenge,
        pollingCallback,
        loginMethod,
        bankIdUrl,
        setBankIdOpened,
        setLoginFailed,
        setUserMessage,
        setQrCode
      );
    } else if (loginMethod === "this_device") {
      checkIfBankIdLoginStarted(
        login_challenge,
        pollingCallback,
        loginMethod,
        setBankIdOpened,
        setLoginFailed,
        setUserMessage
      );
    }
    return () => {
      if (pollingCallback.current) {
        clearTimeout(pollingCallback.current);
        pollingCallback.current = null;
      }
    };
  }, [login_challenge, loginMethod]);

  return (
    <Card variant="elevation" sx={sx} elevation={0}>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Stack spacing={2}>
          <Typography
            sx={{
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: 32,
              lineHeight: "36px",
              textAlign: "center",
            }}
          >
            Logga in hos Capio
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={loginMethod}
            onChange={handleChange}
            exclusive
            fullWidth
            sx={{
              background: "#EEF2F8",
              borderRadius: 2,
              "& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped:not(:first-of-type), & .MuiToggleButtonGroup-grouped:not(:last-of-type)":
                {
                  color: "primary.main",
                  m: 1,
                  borderRadius: 2,
                  borderWidth: 0,
                  textTransform: "none",
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: "24px",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#FFFFFF",
                  },
                  "&:hover:not(.Mui-selected)": {
                    backgroundColor: "#D1DFF1",
                  },
                },
            }}
          >
            <ToggleButton value="this_device">BankID</ToggleButton>
            <ToggleButton value="mobile">BankID på annan enhet</ToggleButton>
          </ToggleButtonGroup>
          {renderedLoginMethod()}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BankIdFrame;
